import { Box, chakra, Field, Icon, Input, Text } from '@chakra-ui/react'
import type { DocumentCountry } from '@ipc/shared'
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { LuCheck } from 'react-icons/lu'
import { useT } from '~/i18n/use-t'

/** Israel and Palestine come first (owner request, spec §12.1 step 1); the rest follows alphabetically. */
export const PRIORITY_COUNTRIES = ['IL', 'PS'] as const

/** Priority countries in their fixed order, then the others by label. */
export function orderCountries(countries: readonly DocumentCountry[]): DocumentCountry[] {
  const rank = (c: DocumentCountry) => {
    const i = (PRIORITY_COUNTRIES as readonly string[]).indexOf(c.iso2)
    return i < 0 ? PRIORITY_COUNTRIES.length : i
  }
  return [...countries].sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label, 'en'))
}

const Option = chakra('li')

/**
 * A filterable country list (ARIA combobox with a listbox: the input keeps focus, the arrow keys move the active
 * option, `aria-activedescendant` names it). The list is open until a country is chosen, so a phone shows the first
 * countries without a tap; choosing one closes it and the input shows the country.
 */
export function CountryPicker({
  countries,
  value,
  onChange,
}: {
  countries: readonly DocumentCountry[]
  /** The chosen country (ISO 3166-1 alpha-2), or null. */
  value: string | null
  onChange: (iso2: string | null) => void
}) {
  const { t } = useT()
  const uid = useId()
  const listId = `${uid}-list`
  const optionId = (iso2: string) => `${uid}-opt-${iso2}`
  const ordered = useMemo(() => orderCountries(countries), [countries])
  const selected = ordered.find((c) => c.iso2 === value) ?? null
  const [typed, setTyped] = useState<string | null>(null)
  const [open, setOpen] = useState(value === null)
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  const query = (typed ?? '').trim().toLocaleLowerCase('en')
  const shown = useMemo(
    () => (query ? ordered.filter((c) => c.label.toLocaleLowerCase('en').includes(query)) : ordered),
    [ordered, query]
  )
  const activeCountry = open ? shown[Math.min(active, shown.length - 1)] : undefined

  // Keep the active option in view when the arrows move it.
  useEffect(() => {
    if (!activeCountry) return
    listRef.current?.querySelector(`[id="${optionId(activeCountry.iso2)}"]`)?.scrollIntoView?.({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCountry?.iso2])

  const choose = (c: DocumentCountry) => {
    onChange(c.iso2)
    setTyped(null)
    setOpen(false)
  }

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setOpen(true)
      else setActive((i) => Math.min(i + 1, shown.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Home' && open) {
      e.preventDefault()
      setActive(0)
    } else if (e.key === 'End' && open) {
      e.preventDefault()
      setActive(Math.max(shown.length - 1, 0))
    } else if (e.key === 'Enter') {
      // Never submit the form from the country box.
      e.preventDefault()
      if (open && activeCountry) choose(activeCountry)
    } else if (e.key === 'Escape' && open) {
      e.preventDefault()
      setOpen(false)
      setTyped(null)
    }
  }

  return (
    <Field.Root>
      <Field.Label>{t('join.document.countryLabel')}</Field.Label>
      <Input
        role='combobox'
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete='list'
        aria-activedescendant={activeCountry ? optionId(activeCountry.iso2) : undefined}
        autoComplete='off'
        autoCapitalize='off'
        spellCheck={false}
        value={typed ?? selected?.label ?? ''}
        onFocus={() => {
          if (value === null) setOpen(true)
        }}
        onBlur={() => {
          setOpen(false)
          setTyped(null)
        }}
        onClick={() => setOpen(true)}
        onKeyDown={onKey}
        onChange={(e) => {
          setTyped(e.target.value)
          setActive(0)
          setOpen(true)
          // Typing something new takes the choice back: the classes below belong to the chosen country.
          if (value !== null) onChange(null)
        }}
      />
      {open && (
        <Box
          as='ul'
          ref={listRef}
          id={listId}
          role='listbox'
          aria-label={t('join.document.countryList')}
          listStyleType='none'
          m={0}
          p={0}
          w='full'
          maxH='15rem'
          overflowY='auto'
          borderWidth='1px'
          borderColor='border.input'
          borderRadius='md'
          bg='bg'
        >
          {shown.length === 0 && (
            <Text as='li' role='presentation' px={4} py={3} color='fg.muted'>
              {t('join.document.countryNone')}
            </Text>
          )}
          {shown.map((c) => {
            const isActive = activeCountry?.iso2 === c.iso2
            return (
              <Option
                key={c.iso2}
                id={optionId(c.iso2)}
                role='option'
                aria-selected={c.iso2 === value}
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                gap={3}
                minH='12'
                px={4}
                py={2}
                cursor='pointer'
                bg={isActive ? 'bg.muted' : 'transparent'}
                _hover={{ bg: 'bg.muted' }}
                // The input keeps focus: a press on an option must not blur it first.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(c)}
              >
                <span>{c.label}</span>
                {c.iso2 === value && <Icon as={LuCheck} aria-hidden boxSize={5} flexShrink={0} />}
              </Option>
            )
          })}
        </Box>
      )}
    </Field.Root>
  )
}
